import { Fragment, useState, type RefObject } from "react";
import { MessageBubble } from "./MessageBubble";
import type { LoadImage } from "./LazyImage";
import { dayKey, dateLabel, timeLabel } from "../format";
import { renderMarkdown } from "../md";
import type { Mention } from "../mentions";
import type { CardState, ChatMessage } from "../protocol";

/** PeerLine is a message the agent exchanged with another agent: one line naming it, the text on a click. */
export function PeerLine({ m, onOpenSession }: { m: ChatMessage; onOpenSession?: (session: string) => void }) {
  const [open, setOpen] = useState(false);
  const p = m.peer!;
  const session = p.session;
  return (
    <div className={`peer-line ${p.dir}`} data-msg-id={m.id}>
      <div className="peer-head">
        <button className="peer-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span className="peer-caret">{open ? "▾" : "▸"}</span> {p.dir === "out" ? "Sent to" : "Message from"}
        </button>{" "}
        {session && onOpenSession ? (
          <button className="peer-name" title={`Open ${p.name}'s chat`} onClick={() => onOpenSession(session)}>
            {p.name}
          </button>
        ) : (
          <span className="peer-name">{p.name}</span>
        )}
        <span className="time">{timeLabel(m.ts)}</span>
      </div>
      {open && <div className="peer-body md" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />}
    </div>
  );
}

/** The scrolling chat transcript: load-older hint, skeleton while loading, date separators, and bubbles. */
export function ChatMessages({
  messages,
  chatReady,
  chatLimit,
  messagesRef,
  onScroll,
  endRef,
  load,
  onOpen,
  onOpenDoc,
  onOption,
  cardStates,
  onCardDismiss,
  unreadFromId,
  live,
  onOpenSession,
  sessionNames,
}: {
  messages: ChatMessage[];
  chatReady: boolean;
  chatLimit: number;
  messagesRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  endRef: RefObject<HTMLDivElement>;
  load: LoadImage;
  onOpen: (url: string) => void;
  onOpenDoc: (title: string, docId: string) => void;
  onOption: (text: string, extra?: { cardId?: string; optionId?: string }) => void;
  cardStates: Map<string, CardState>;
  onCardDismiss: (cardId: string) => void;
  /** The message the chat opens on — gets the "Unread" rule above it. Null when nothing is unread. */
  unreadFromId?: string | null;
  /** What the agent is doing now, shown below the last message until it goes quiet. */
  live?: string;
  /** Opens another session's chat, by its session id: the other side of an agent-to-agent message. */
  onOpenSession?: (session: string) => void;
  /** The sessions an `@name` in a message can point at. */
  sessionNames?: Mention[];
}) {
  // Resolve markers carry no prose — they only retract a card, so they must not become bubbles.
  const visible = messages.filter((m) => !m.sys || (m.sys === "agent_run" && m.text));
  return (
    <div className="messages" ref={messagesRef} onScroll={onScroll}>
      {chatReady && messages.length >= chatLimit && (
        <div className="load-older">↑ Scroll up for earlier messages</div>
      )}
      {!chatReady ? (
        <div className="skeleton-chat">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className={`skeleton-bubble ${i % 2 ? "user" : "agent"}`} />
          ))}
        </div>
      ) : (
        visible.map((m, i) => (
          <Fragment key={m.id}>
            {/* Sits above the first unread and stays put: displaying a message marks it read, so a
                divider recomputed from live state would vanish as the user looked at it. */}
            {m.id === unreadFromId && (
              <div className="unread-sep" data-unread-anchor>
                <span>Unread</span>
              </div>
            )}
            {(i === 0 || dayKey(m.ts) !== dayKey(visible[i - 1].ts)) && (
              <div className="date-sep">
                <span>{dateLabel(m.ts)}</span>
              </div>
            )}
            {m.sys === "agent_run" ? (
              <div className="date-sep sys-line">
                <span>{m.text}</span>
              </div>
            ) : m.peer ? (
              <PeerLine m={m} onOpenSession={onOpenSession} />
            ) : (
            <MessageBubble
              m={m}
              load={load}
              onOpen={onOpen}
              onOpenDoc={onOpenDoc}
              onOption={onOption}
              cardState={m.card ? cardStates.get(m.card.id) : undefined}
              onCardDismiss={onCardDismiss}
              quoted={m.replyTo ? (messages.find((x) => x.id === m.replyTo)?.text ?? "") : undefined}
              sessionNames={sessionNames}
              onOpenSession={onOpenSession}
            />
            )}
          </Fragment>
        ))
      )}
      {chatReady && live && (
        <div className="live-status" aria-live="polite">
          <div className="live-update" key={live}>
            {live}
          </div>
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}
